import { EpiFormsValidatorMetaData } from "../../Shared/Assets/src/types/epi-forms";
import { setPurposeErrorVisible } from "./errors";
import {
  areRequiredConsentCheckboxesChecked,
  hasValidContactInfo,
  isValidOneTrustPhone
} from "./validity";

// Pairs with the server-side no-op AniCura.Web.Features.Blocks.ElementBlocks.SalesforceConsentElementBlock.OneTrustConsentValidator.
// Actual validation happens here, since the checkboxes are rendered client-side and never become
// the element's own bound value.
export const registerOneTrustConsentValidator = (): void => {
  const forms = window.epi?.EPiServer?.Forms;

  if (forms) {
    forms.Validators[
      "AniCura.Web.Features.Blocks.ElementBlocks.SalesforceConsentElementBlock.OneTrustConsentValidator"
    ] = function (
      fieldName: string,
      _: string,
      validatorMetaData: EpiFormsValidatorMetaData
    ) {
      const marker = document.querySelector<HTMLElement>(
        `[data-f-element-name="${fieldName}"] [data-ssocc-touchpoint]`
      );

      if (marker) {
        const requiredCheckboxes = Array.from(
          marker.querySelectorAll<HTMLInputElement>('input[type="checkbox"][required]')
        );

        const message = validatorMetaData?.model?.message;

        requiredCheckboxes.forEach(checkbox => {
          const purposeElement = checkbox.closest<HTMLElement>(".ssocc-sdk-purpose");

          if (purposeElement) {
            setPurposeErrorVisible(purposeElement, !checkbox.checked, message);
          }
        });

        // The per-checkbox errors above already cover this, so no message is passed up here -
        // otherwise it would also be shown in the element's own shared validation message slot,
        // which is also used by OneTrustContactRequiredValidator.
        if (!areRequiredConsentCheckboxesChecked(marker)) {
          return { isValid: false, message: "" };
        }
      }

      return { isValid: true };
    };
  }
};

// Pairs with the server-side no-op AniCura.Web.Features.Blocks.ElementBlocks.PhonenumberElementBlock.PhoneNumberE164Validator.
export const registerPhoneNumberE164Validator = (): void => {
  const forms = window.epi?.EPiServer?.Forms;

  if (forms) {
    forms.Validators[
      "AniCura.Web.Features.Blocks.ElementBlocks.PhonenumberElementBlock.PhoneNumberE164Validator"
    ] = function (
      _fieldName: string,
      fieldValue: string,
      validatorMetaData: EpiFormsValidatorMetaData
    ) {
      if (!fieldValue) {
        return { isValid: true };
      }

      return {
        isValid: isValidOneTrustPhone(fieldValue),
        message: validatorMetaData?.model?.message
      };
    };
  }
};

// Pairs with the server-side no-op AniCura.Web.Features.Blocks.ElementBlocks.SalesforceConsentElementBlock.OneTrustContactRequiredValidator.
// The touchpoint decides which of the email/phone fields must be present and valid (at least one
// of them, or e.g. both for marketing-mandatory-email-phone - see getContactRequirement); since
// that spans several elements, it's checked here against whichever exist in the form.
export const registerOneTrustContactRequiredValidator = (): void => {
  const forms = window.epi?.EPiServer?.Forms;

  if (forms) {
    forms.Validators[
      "AniCura.Web.Features.Blocks.ElementBlocks.SalesforceConsentElementBlock.OneTrustContactRequiredValidator"
    ] = function (
      fieldName: string,
      _: string,
      validatorMetaData: EpiFormsValidatorMetaData
    ) {
      const marker = document.querySelector<HTMLElement>(
        `[data-f-element-name="${fieldName}"] [data-ssocc-touchpoint]`
      );

      const form = marker?.closest("form");

      if (marker && form instanceof HTMLFormElement) {
        if (hasValidContactInfo(form, marker)) {
          return { isValid: true };
        } else {
          return { isValid: false, message: validatorMetaData?.model?.message };
        }
      } else {
        return { isValid: true };
      }
    };
  }
};
